import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../lib/api'
import type {
  PharmaDeadStockFilters,
  PharmaDeadStockList,
  PharmaDimensions,
  PharmaExpiryRiskFilters,
  PharmaExpiryRiskList,
  PharmaKpis,
  PharmaKpisFilters,
  PharmaMovementsFilters,
  PharmaMovementsList,
  PharmaTurnoverFilters,
  PharmaTurnoverList,
} from '../types/pharma-analytics.types'

const PHARMA_QUERY_KEY = 'pharma-analytics'

/**
 * Витрины считаются по ночному срезу остатков — в течение дня они не меняются,
 * поэтому staleTime заметно больше, чем у оперативных списков.
 */
const STALE_TIME = 5 * 60 * 1000

export const usePharmaKpis = (filters: PharmaKpisFilters = {}) =>
  useQuery({
    queryKey: [PHARMA_QUERY_KEY, 'kpis', filters],
    queryFn: async (): Promise<PharmaKpis> => apiClient.getPharmaKpis(filters),
    staleTime: STALE_TIME,
    retry: 1,
  })

export const usePharmaExpiryRisk = (
  filters: PharmaExpiryRiskFilters = {},
  enabled = true,
) =>
  useQuery({
    queryKey: [PHARMA_QUERY_KEY, 'expiry-risk', filters],
    queryFn: async (): Promise<PharmaExpiryRiskList> =>
      apiClient.getPharmaExpiryRisk(filters),
    staleTime: STALE_TIME,
    enabled,
    retry: 1,
  })

export const usePharmaDeadStock = (
  filters: PharmaDeadStockFilters = {},
  enabled = true,
) =>
  useQuery({
    queryKey: [PHARMA_QUERY_KEY, 'dead-stock', filters],
    queryFn: async (): Promise<PharmaDeadStockList> =>
      apiClient.getPharmaDeadStock(filters),
    staleTime: STALE_TIME,
    enabled,
    retry: 1,
  })

export const usePharmaTurnover = (
  filters: PharmaTurnoverFilters = {},
  enabled = true,
) =>
  useQuery({
    queryKey: [PHARMA_QUERY_KEY, 'turnover', filters],
    queryFn: async (): Promise<PharmaTurnoverList> =>
      apiClient.getPharmaTurnover(filters),
    staleTime: STALE_TIME,
    enabled,
    retry: 1,
  })

export const usePharmaMovements = (
  filters: PharmaMovementsFilters = {},
  enabled = true,
) =>
  useQuery({
    queryKey: [PHARMA_QUERY_KEY, 'movements', filters],
    queryFn: async (): Promise<PharmaMovementsList> =>
      apiClient.getPharmaMovements(filters),
    staleTime: STALE_TIME,
    enabled,
    retry: 1,
  })

/** Справочники фильтров и границы данных — меняются раз в сутки. */
export const usePharmaDimensions = () =>
  useQuery({
    queryKey: [PHARMA_QUERY_KEY, 'dimensions'],
    queryFn: async (): Promise<PharmaDimensions> =>
      apiClient.getPharmaDimensions(),
    staleTime: 30 * 60 * 1000,
    retry: 1,
  })
